import React, { useEffect, useRef, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Chip, Card, CardBody } from "@heroui/react";
import clsx from 'clsx';

import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
  useDisclosure,
  addToast,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  SelectItem
} from "@heroui/react";
import { MoreVertical, PencilIcon, RotateCcw, Eye, Pencil, Trash, Search, Filter, X, Plus, Tags } from "lucide-react";
import NewSessionModal from "../components/session/NewSessionModal";
import AppButton from "../components/common/AppButton";
import UploadDrawer from "../components/session/UploadDrawer";
import axios from "axios";
import { getTranscriptionsPaginatedAsync, deleteTranscriptionAsync } from "../redux/transcription";
import { getAllTagsAsync, createTagAsync } from "../redux/tag";
import { getAllTranscriptionTagsAsync, createTranscriptionTagAsync, deleteTranscriptionTagAsync } from "../redux/transcriptionTag";
import { useNavigate } from "react-router-dom";
import { useFooter } from "../components/layout/FooterContext";
import AppButtonFooter from "../components/common/AppButtonFooter";
import AppButtonHome from "../components/common/AppButtonHome";
import useIntervalWhile from "../hooks/useIntervalWhile";
import { hasSessionsInProgress, isSpeakersNotice } from "../utils/sessionStages";

// How often the list refreshes while a session on the page is processing.
const SESSION_REFRESH_MS = 15000;


// Stages that mean the run is over and unsuccessful. These MUST be
// listed: the label/colour lookups fall back to "In Bearbeitung", so an
// unmapped failure renders as if the session were still working and the
// user has no idea anything went wrong.
const FAILED_STAGES = new Set(["gpt failed", "failed", "transcription failed"]);

const isFailedStage = (stage) =>
  FAILED_STAGES.has(String(stage || "").toLowerCase());

const statusColors = {
  "completed": "success",
  "in progress": "primary",
  "waiting upload": "warning",
  "transcription pending": "warning",
  "transcription running":"warning",
  "correcting with gpt": "warning",
  "correcting with gpt2": "warning",
  // Fast Transcription stages — previously unmapped, so they all showed
  // the generic fallback with no colour.
  "fast: queued": "warning",
  "fast: downloading audio": "warning",
  "fast: converting audio": "warning",
  "fast: transcribing": "warning",
  "fast: persisting words": "warning",
  "fast: assembling": "warning",
  "gpt failed": "danger",
  "failed": "danger",
  "transcription failed": "danger",
};

const statusName = {
  "completed": "Fertig",
  "in progress": "In Bearbeitung",
  "correcting with gpt": "Nachbearbeitung",
  "correcting with gpt2": "Nachbearbeitung",
  "transcription running":"Transkription",
  "waiting upload": "Warte auf Upload",
  "transcription pending": "Wartet",
  "fast: queued": "In Warteschlange",
  "fast: downloading audio": "Audio wird geladen",
  "fast: converting audio": "Audio wird konvertiert",
  "fast: transcribing": "Transkription",
  "fast: persisting words": "Transkription",
  "fast: assembling": "Transkription",
  "gpt failed": "Fehlgeschlagen",
  "failed": "Fehlgeschlagen",
  "transcription failed": "Fehlgeschlagen",
}

const formatStatus = (status) => {
  const key = String(status || "").toLowerCase();
  return {
    label: statusName[key] || "In Bearbeitung",
    variant: statusColors[key] || "default",
  };
};

const Home = () => {
  const { paginatedTranscriptions, totalTranscriptions, currentPage, pageSize, paginatedRequestId } = useSelector((state) => state.transcriptions);
  const { tags } = useSelector((state) => state.tags);
  const { relations } = useSelector((state) => state.transcriptionTags);
  const transcriptionsList = Array.isArray(paginatedTranscriptions) ? paginatedTranscriptions : [];
  const totalPages = Math.ceil(totalTranscriptions / pageSize) || 1;
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isOpen: isDeleteModalOpen, onOpen: openDeleteModal, onOpenChange: onDeleteModalChange } = useDisclosure();
  const { isOpen: isRegenerateModalOpen, onOpen: openRegenerateModal, onOpenChange: onRegenerateModalChange } = useDisclosure();
  const [sessionToRegenerate, setSessionToRegenerate] = useState(null);
  const { isOpen: isCreateTagModalOpen, onOpen: openCreateTagModal, onOpenChange: onCreateTagModalChange } = useDisclosure();
  const { isOpen: isAssociateTagModalOpen, onOpen: openAssociateTagModal, onOpenChange: onAssociateTagModalChange } = useDisclosure();
  const [sessionToDelete, setSessionToDelete] = useState(null);
  
  // Upload drawer state
  const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  // Simple filters
  const [searchText, setSearchText] = useState("");
  const searchTimerRef = useRef(null);
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [selectedTagFilters, setSelectedTagFilters] = useState(new Set());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [uploadedBy, setUploadedBy] = useState("all"); // 'me' | 'all'
  const [newTagName, setNewTagName] = useState("");
  const [currentTagSelection, setCurrentTagSelection] = useState(new Set());
  const [tagAssociationTarget, setTagAssociationTarget] = useState(null);

  const { setFooterContent, setTitle } = useFooter();

  // Get unique statuses for filter dropdown
  const availableStatuses = useMemo(() => {
    const statuses = new Set();
    transcriptionsList.forEach(session => {
      if (session.stage) {
        statuses.add(session.stage);
      }
    });
    return Array.from(statuses).sort();
  }, [transcriptionsList]);

  const tagsById = useMemo(() => {
    const map = new Map();
    (tags || []).forEach((tag) => {
      map.set(tag.id, tag);
    });
    return map;
  }, [tags]);

  const transcriptionTagMap = useMemo(() => {
    const map = new Map();
    (relations || []).forEach((relation) => {
      const tag = relation.tag || tagsById.get(relation.tag_id);
      if (!tag) return;
      if (!map.has(relation.transcription_id)) {
        map.set(relation.transcription_id, []);
      }
      map.get(relation.transcription_id).push(tag);
    });
    return map;
  }, [relations, tagsById]);

  // All filters (search, date range, status, tags) are applied
  // server-side via `fetchPage`, so pagination reflects the FILTERED
  // set. Any legacy pre-filter or presentation transform belongs here;
  // for now the rows come back ready-to-render.
  const filteredTranscriptions = transcriptionsList;

  useEffect(() => {
    setFooterContent(
        <AppButtonFooter
            onClick={onOpen}
            width="w-[120px]"
            rounded="rounded-[16px]"
        >
          Neue Sitzung
        </AppButtonFooter>
    );
    setTitle("Sitzungen")

    return () => setFooterContent(null); // Clean up on unmount
  }, []);

  const fetchPage = (
    page = 1,
    search = searchText,
    dFrom = fromDate,
    dTo = toDate,
    statusSet = selectedStatuses,
    tagSet = selectedTagFilters,
  ) => {
    const stages = statusSet && statusSet.size > 0
      ? Array.from(statusSet)
      : [];
    const tag_ids = tagSet && tagSet.size > 0
      ? Array.from(tagSet)
          .map((k) => parseInt(k, 10))
          .filter((n) => !Number.isNaN(n))
      : [];
    dispatch(
      getTranscriptionsPaginatedAsync({
        page,
        page_size: pageSize || 20,
        search,
        date_from: dFrom || "",
        date_to: dTo || "",
        stages,
        tag_ids,
      })
    );
  };

  useEffect(() => {
    fetchPage(1, "", "", "", new Set(), new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Debounced server-side search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchPage(1, searchText, fromDate, toDate, selectedStatuses, selectedTagFilters);
    }, 400);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // Refetch immediately when any filter changes — no debounce needed
  // since users pick these atomically rather than typing.
  useEffect(() => {
    fetchPage(1, searchText, fromDate, toDate, selectedStatuses, selectedTagFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, selectedStatuses, selectedTagFilters]);

  // Keep the stage column current while the backend works on a session on
  // this page, so progress, completion and failures show without a reload.
  useIntervalWhile(
    () => {
      // Not over a request of the user's (a search, a filter, another
      // page): it would ask for the old page under the new filters.
      if (!paginatedRequestId) fetchPage(currentPage);
    },
    hasSessionsInProgress(paginatedTranscriptions),
    SESSION_REFRESH_MS
  );

  useEffect(() => {
    dispatch(getAllTagsAsync());
    dispatch(getAllTranscriptionTagsAsync());
  }, [dispatch]);
  
  const openUploadDrawer = (session) => {
    setSelectedSession(session);
    setUploadDrawerOpen(true);
  };

const deleteSession = async (sessionId) => {
  if (!window.confirm("Are you sure you want to delete this session?")) return;

  try {
    await dispatch(deleteTranscriptionAsync(sessionId)).unwrap();

    try {
      const stored = localStorage.getItem("recentSessions");
      if (stored) {
        const recent = JSON.parse(stored).filter((s) => String(s.id) !== String(sessionId));
        localStorage.setItem("recentSessions", JSON.stringify(recent));
        window.dispatchEvent(new CustomEvent("recent-sessions-updated"));
      }
    } catch (_) {}

    addToast({
      title: "Sitzung erfolgreich gelöscht",
      color: "success",
    });
  } catch (err) {
    console.error("Failed to delete session:", err);
    addToast({
      title: "Fehler beim Löschen der Sitzung",
      color: "danger",
    });
  }
};

  const startTranscription = async (session) => {
    try {
      const payload = {
        "file_name": session.id+".mp3",
        "session_id": session.id
      }
      const res = await axios.post(`wav_transcript_000073_1_0_0/start_transcription_from_frontend`, payload);

      fetchPage(currentPage);

      console.log("Upload complete and session updated.");
      addToast({
        title: "Transkription gestartet",
        color: "success"
      });
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Something went wrong during start of the transcription.");
    }
  };

  // Fast Transcription path: replaces the batch + polling flow with a
  // single synchronous Azure Fast Transcription call. The backend handles
  // everything (audio download from blob, Fast call, paragraph_words,
  // RTF agenda parsing, GPT correction, speaker naming) in one background
  // job — the user can close the tab and come back when stage hits
  // "Completed". Empirically ~2 min for a 75-min Belgian session vs.
  // 30-60+ min on the old batch path.
  const startFastTranscription = async (session) => {
    try {
      const res = await axios.post(
        `transcriptions_poc_000113_1_0_0/transcribe-fast/${session.id}`
      );
      fetchPage(currentPage);
      if (res?.status === 202) {
        addToast({
          title: "Schnell-Transkription gestartet",
          description: "Läuft im Hintergrund (typisch 2-5 Min)",
          color: "success",
        });
      } else {
        addToast({
          title: "Schnell-Transkription gestartet",
          color: "success",
        });
      }
    } catch (err) {
      console.error("Fast transcription failed to start:", err);
      addToast({
        title: "Schnell-Transkription konnte nicht gestartet werden",
        description: err?.response?.data?.error || err?.message || "",
        color: "danger",
      });
    }
  };

  const checkTranscriptionResult = async (session) => {
    if (!session.batch_transcription_id){
      addToast({
          title: "Transkription in Warteschlange"
        });
      return  
    }
    try {
      const payload = {
        "job_id": session.batch_transcription_id,
        "session_id": session.id,
        "start_time":session.start
      }
      const res = await axios.post(`transcriptions_poc_000113_1_0_0/check_transcription_from_frontend`, payload);

      console.log(res)

      fetchPage(currentPage);

      console.log("Upload complete and session updated.");
      if (res?.data?.status === "Running") {
        addToast({
          title: "Transkription läuft noch"
        });
      } else if (res?.status == 200) {
        addToast({
          title: "Transkription abgeschlossen. GPT-Prüfungen gestartet",
          color: "success"
        });
      } else {
        addToast({
          title: "Unerwartete Antwort vom Server",
          color: "warning"
        });
      }
    } catch (err) {
      console.error("Upload failed:", err);
      // alert("Something went wrong during start of the transcription.");
      addToast({
          title: "Fehler bei der Transkription.",
          color: "danger"
        });
    }
  };

  // Regenerate a session by running the full Fast Transcription pipeline
  // from scratch. Fires the same /transcribe-fast/<id> endpoint that
  // NewSessionModal uses for brand-new sessions. The session keeps its
  // paragraphs, words and headings until the new transcript is stored,
  // which then replaces them, and the endpoint refuses (409) while the
  // session is already being transcribed. Respects the session's stored
  // `languages` so mixed-language sessions take the new two-pass
  // speaker-turn path.
  const regenerateGPTChecks = async (session) => {
    try {
      const parsedLanguages = (() => {
        try {
          const raw = session.languages;
          if (Array.isArray(raw)) return raw;
          if (typeof raw === "string" && raw.trim()) {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : null;
          }
        } catch (e) {
          /* fall through */
        }
        return null;
      })();

      const body = parsedLanguages ? { languages: parsedLanguages } : {};
      const res = await axios.post(
        `transcriptions_poc_000113_1_0_0/transcribe-fast/${session.id}`,
        body
      );

      fetchPage(currentPage);

      if (res?.status === 202) {
        addToast({
          title: "Regenerierung gestartet",
          description: "Sitzung wird von Grund auf neu transkribiert (typisch 3-5 Min)",
          color: "success",
        });
      } else {
        addToast({
          title: "Regenerierung gestartet",
          color: "success",
        });
      }
    } catch (err) {
      console.error("Regenerate failed:", err);
      addToast({
        title: "Regenerierung konnte nicht gestartet werden",
        description: err?.response?.data?.error || err?.message || "",
        color: "danger",
      });
    }
  };

  const handleCreateTag = async (onClose) => {
    if (!newTagName.trim()) {
      addToast({
        title: "Bitte geben Sie einen Tag-Namen ein",
        color: "warning"
      });
      return;
    }

    try {
      await dispatch(createTagAsync({ name: newTagName.trim() })).unwrap();
      await dispatch(getAllTagsAsync());
      setNewTagName("");
      addToast({
        title: "Tag erstellt",
        color: "success"
      });
      onClose?.();
    } catch (err) {
      console.error("Failed to create tag:", err);
      addToast({
        title: "Tag konnte nicht erstellt werden",
        color: "danger"
      });
    }
  };

  const handleOpenAssociateTagModal = (session) => {
    const existingTags = transcriptionTagMap.get(session.id) || [];
    setCurrentTagSelection(
      new Set(existingTags.map((tag) => tag.id.toString()))
    );
    setTagAssociationTarget(session);
    openAssociateTagModal();
  };

  const handleSaveTagAssociations = async (onClose) => {
    if (!tagAssociationTarget) return;
    const desiredTagIds = new Set(
      Array.from(currentTagSelection).map((key) => parseInt(key, 10))
    );
    const existingRelations = (relations || []).filter(
      (relation) => relation.transcription_id === tagAssociationTarget.id
    );
    const existingTagIds = new Set(
      existingRelations.map((relation) => relation.tag_id)
    );

    const createOperations = Array.from(desiredTagIds)
      .filter((id) => !existingTagIds.has(id))
      .map((tagId) =>
        dispatch(
          createTranscriptionTagAsync({
            transcription_id: tagAssociationTarget.id,
            tag_id: tagId,
          })
        ).unwrap()
      );

    const deleteOperations = existingRelations
      .filter((relation) => !desiredTagIds.has(relation.tag_id))
      .map((relation) =>
        dispatch(deleteTranscriptionTagAsync(relation.id)).unwrap()
      );

    try {
      await Promise.all([...createOperations, ...deleteOperations]);
      await dispatch(getAllTranscriptionTagsAsync());
      addToast({
        title: "Tags aktualisiert",
        color: "success",
      });
      onClose?.();
    } catch (err) {
      console.error("Failed to update tags:", err);
      addToast({
        title: "Tags konnten nicht aktualisiert werden",
        color: "danger",
      });
    }
  };

  return (
    <div className="h-[82vh]  overflow-auto bg-transparent rounded-2xl px-3 border-0">

      {/* Modal */}
      <NewSessionModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onCreated={() => fetchPage(1, "")}
      />

      <Modal isOpen={isDeleteModalOpen} onOpenChange={onDeleteModalChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Sitzung löschen</ModalHeader>
              <ModalBody>
                <p>Möchten Sie die Sitzung <strong>{sessionToDelete?.name}</strong> wirklich löschen?</p>
                <p>Dies kann nicht rückgängig gemacht werden.</p>
              </ModalBody>
              <ModalFooter>
                <Button color="default" variant="bordered" radius="full" onPress={onClose}>
                  Abbrechen
                </Button>
                <Button
                  color="danger"
                  onPress={async () => {
                    try {
                      await dispatch(deleteTranscriptionAsync(sessionToDelete.id)).unwrap();
                      try {
                        const stored = localStorage.getItem("recentSessions");
                        if (stored) {
                          const recent = JSON.parse(stored).filter((s) => String(s.id) !== String(sessionToDelete.id));
                          localStorage.setItem("recentSessions", JSON.stringify(recent));
                          window.dispatchEvent(new CustomEvent("recent-sessions-updated"));
                        }
                      } catch (_) {}
                      addToast({ title: "Sitzung gelöscht", color: "success" });
                    } catch (err) {
                      console.error("Delete failed:", err);
                      addToast({ title: "Fehler beim Löschen", color: "danger" });
                    }
                    onClose();
                  }}
                >
                  Löschen
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal isOpen={isRegenerateModalOpen} onOpenChange={onRegenerateModalChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Sitzung neu transkribieren</ModalHeader>
              <ModalBody>
                <p>
                  Möchten Sie die Sitzung{" "}
                  <strong>{sessionToRegenerate?.name}</strong>{" "}
                  von Grund auf neu transkribieren?
                </p>
                <p className="text-sm text-onSurface/70">
                  Bestehende Absätze, Überschriften und manuelle Änderungen
                  (Textkorrekturen, Sprechernamen) werden ersetzt. Die
                  Regenerierung dauert typischerweise 3-5 Minuten. Hervorhebungen
                  bleiben erhalten.
                </p>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="bordered" radius="full" onPress={onClose}>
                  Abbrechen
                </Button>
                <AppButton
                  width="w-[170px]"
                  onClick={async () => {
                    if (sessionToRegenerate) {
                      await regenerateGPTChecks(sessionToRegenerate);
                    }
                    setSessionToRegenerate(null);
                    onClose();
                  }}
                >
                  Neu transkribieren
                </AppButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal isOpen={isCreateTagModalOpen} onOpenChange={onCreateTagModalChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Neuen Tag erstellen</ModalHeader>
              <ModalBody>
                <Input
                  label="Tag-Name"
                  variant="bordered"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="z.B. Vorstand"
                />
              </ModalBody>
              <ModalFooter className="flex gap-3">
                <AppButton
                  onClick={onClose}
                  width="w-[120px]"
                  roundedFull="rounded-[16px]"
                  className="bg-surfaceVariant text-onSurfaceVariant hover:bg-surface"
                >
                  Abbrechen
                </AppButton>
                <AppButton
                  onClick={() => handleCreateTag(onClose)}
                  width="w-[140px]"
                  roundedFull="rounded-[16px]"
                >
                  Erstellen
                </AppButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal isOpen={isAssociateTagModalOpen} onOpenChange={onAssociateTagModalChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                {tagAssociationTarget
                  ? `Tags für "${tagAssociationTarget.name}" auswählen`
                  : "Tags auswählen"}
              </ModalHeader>
              <ModalBody>
                <Select
                  label="Tags"
                  placeholder="Wähle Tags"
                  selectionMode="multiple"
                  variant="bordered"
                  selectedKeys={currentTagSelection}
                  onSelectionChange={setCurrentTagSelection}
                  aria-label="Tags für Sitzung auswählen"
                >
                  {(tags || []).map((tag) => (
                    <SelectItem key={tag.id.toString()} value={tag.id.toString()}>
                      {tag.name}
                    </SelectItem>
                  ))}
                </Select>
              </ModalBody>
              <ModalFooter className="flex gap-3">
                <AppButton
                  onClick={onClose}
                  width="w-[120px]"
                  roundedFull="rounded-[16px]"
                  className="bg-surfaceVariant text-onSurfaceVariant hover:bg-surface"
                >
                  Abbrechen
                </AppButton>
                <AppButton
                  onClick={() => handleSaveTagAssociations(onClose)}
                  width="w-[140px]"
                  roundedFull="rounded-[16px]"
                >
                  Speichern
                </AppButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <UploadDrawer
        isOpen={uploadDrawerOpen}
        onClose={() => setUploadDrawerOpen(false)}
        session={selectedSession}
      />

        <div className="bg-surface rounded-2xl h-[78vh] overflow-scroll">
          {/* Filter Section */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-wrap items-center gap-3">
              {/* Free Text Search */}
              <div className="flex-1 min-w-0">
                <Input
                  placeholder="Sitzungen suchen..."
                  value={searchText}
                  onValueChange={setSearchText}
                  startContent={<Search className="w-4 h-4 text-gray-400" />}
                  variant="bordered"
                  size="sm"
                />
              </div>

              {/* Date Range */}
              <Input
                type="date"
                label="Von"
                value={fromDate}
                onValueChange={setFromDate}
                variant="bordered"
                size="sm"
                className="w-32"
              />

              <Input
                type="date"
                label="bis"
                value={toDate}
                onValueChange={setToDate}
                variant="bordered"
                size="sm"
                className="w-32"
              />

              {/* Uploaded By */}
              {/* <Select
                selectedKeys={[uploadedBy]}
                onSelectionChange={(keys) => setUploadedBy(Array.from(keys)[0])}
                variant="bordered"
                size="sm"
                className="w-36"
                aria-label="Filter by uploader"
              >
                <SelectItem key="all" value="all">All Sessions</SelectItem>
                <SelectItem key="me" value="me">My Sessions</SelectItem>
              </Select> */}

              {/* Status Selector */}
              <Select
                selectedKeys={selectedStatuses}
                onSelectionChange={setSelectedStatuses}
                variant="bordered"
                size="sm"
                className="w-40"
                selectionMode="multiple"
                placeholder="Status"
                aria-label="Filter by status"
              >
                {availableStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    { statusName[status.toLowerCase()]}
                  </SelectItem>
                ))}
              </Select>

              {/* Tag Selector */}
              <Select
                selectedKeys={selectedTagFilters}
                onSelectionChange={setSelectedTagFilters}
                variant="bordered"
                size="sm"
                className="w-48"
                selectionMode="multiple"
                placeholder="Tags"
                aria-label="Filter by tags"
              >
                {(tags || []).map((tag) => (
                  <SelectItem key={tag.id.toString()} value={tag.id.toString()}>
                    {tag.name}
                  </SelectItem>
                ))}
              </Select>

              <AppButtonHome
                onClick={openCreateTagModal}
                width="w-[150px]"
                roundedFull="rounded-[16px]"
                className="ml-auto"
              >
                <Plus className="w-4 h-4" />
                Tag erstellen
              </AppButtonHome>

              {/* Clear All Button */}
              {(searchText || selectedStatuses.size > 0 || selectedTagFilters.size > 0 || fromDate || toDate || uploadedBy !== "all") && (
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  startContent={<X className="w-4 h-4" />}
                  onPress={() => {
                    setSearchText("");
                    setSelectedStatuses(new Set());
                    setSelectedTagFilters(new Set());
                    setFromDate("");
                    setToDate("");
                    setUploadedBy("all");
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          <ul className="divide-y divide-gray-200 p-0">
            {Array.isArray(filteredTranscriptions) && filteredTranscriptions.map((session) => {
              const { label, variant } = formatStatus(session.stage || "in progress");
              const sessionTags = transcriptionTagMap.get(session.id) || [];

              return (
                <li
                  key={session.id}
                  className="cursor-pointer hover:bg-secondaryContainer hover:rounded-xl transition-shadow"
                >
                  <div className="px-6 py-4">
                    <div className="flex justify-between items-start md:items-center gap-3" onClick={() =>{
                        if(session.stage == "Completed"){
                          navigate(`/session/${session.id}`)

                        }
                      }
                    } >
                      
                      {/* Left: Session Info.
                          min-w-0 on every flex ancestor of the status chip:
                          a flex item defaults to min-width:auto, so it refuses
                          to shrink below its content and a long error message
                          widens the whole page instead of truncating. */}
                      <div className="flex flex-col gap-1 min-w-0">
                        <div className="text-base font-semibold text-gray-900 flex flex-wrap items-center gap-2 min-w-0">
                          {session.name}
                          {session.stage?.toLowerCase() !== "completed" && (
                            <Chip variant="bordered"
                                  className={clsx(
                                    'text-xs px-2 py-0.5 bg-gray-100 border-0 shrink-0',
                                  )}>
                              {label}
                            </Chip>
                          )}
                          {session.correction_status &&
                            (session.stage != "Completed" || isSpeakersNotice(session.correction_status)) &&
                            /* correction_status carries both progress text and
                               raw exception strings, so it is sized to whatever
                               is left on the line and clipped. The full value
                               stays reachable via the native tooltip. On a
                               finished session it is only shown when it says
                               the speakers could not be named. */
                            <Chip variant="bordered"
                            title={session.correction_status}
                            classNames={{ content: "truncate block" }}
                            className={clsx(
                              'text-xs px-2 py-0.5 border-0 min-w-0 max-w-full',
                              isFailedStage(session.stage)
                                ? 'bg-red-50 text-red-700'
                                : isSpeakersNotice(session.correction_status)
                                ? 'bg-amber-50 text-amber-800'
                                : 'bg-gray-100',
                            )}>
                              {session.correction_status}
                            </Chip>
                          }
                        </div>
                        <div className="text-sm text-gray-500 flex flex-wrap items-center gap-2">
                          <span>{session.start.split("T")[0]}</span>
                          <span>{session.start.split("T")[1].split("Z")[0]}</span>
                          <strong>Tags:</strong>
                          {sessionTags.length > 0 &&
                            sessionTags.map((tag) => (
                              <Chip
                                key={`${session.id}-tag-${tag.id}`}
                                variant="bordered"
                                className={clsx('text-xs px-2 py-0.5 bg-blue-100 border-0')}
                              >
                                {tag.name}
                              </Chip>
                            ))}
                        </div>
                      </div>

                      {/* Right: Action Buttons */}
                      <div className="flex items-center gap-2 flex-shrink-0 self-start md:self-center">
                        

                        {/* View button only on md+ screens */}
                        { session.stage == "Completed" && (

                          <AppButtonHome
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/session/${session.id}`);
                            }}
                            width="w-[140px]"
                            rounded="rounded-[16px]"
                            className="hidden md:inline-flex"
                          >
                            Sitzung Öffnen
                          </AppButtonHome>
                          ) 
                        }
                        
                        { session.stage == "Waiting upload" && (

                          <AppButtonHome
                            onClick={(e) => {
                              e.stopPropagation();
                              openUploadDrawer(session);
                            }}
                            
                            width="w-[140px]"
                            rounded="rounded-[16px]"
                            className="hidden md:inline-flex"
                          >
                              Upload File
                          </AppButtonHome>
                          ) 
                        }

                        { session.stage == "Transcription pending" && (
                          <>
                            <AppButtonHome
                              onClick={(e) => {
                                e.stopPropagation();
                                startFastTranscription(session)
                              }}
                              width="w-[170px]"
                              rounded="rounded-[16px]"
                              className="hidden md:inline-flex"
                            >
                              Schnell-Transkription
                            </AppButtonHome>
                            <AppButtonHome
                              onClick={(e) => {
                                e.stopPropagation();
                                startTranscription(session)
                              }}
                              width="w-[140px]"
                              rounded="rounded-[16px]"
                              className="hidden md:inline-flex"
                            >
                              Start Transcription
                            </AppButtonHome>
                          </>
                        )
                        }


                        { session.stage == "Transcription running2" && (

                          <AppButtonHome
                            onClick={(e) => {
                              e.stopPropagation();
                              checkTranscriptionResult(session)
                            }}
                            width="w-[140px]"
                            rounded="rounded-[16px]"
                            className="hidden md:inline-flex"
                          >
                            Korrektur Starten
                          </AppButtonHome>
                        ) 
                        }




                        {/* 3-dot dropdown shown on all screens */}
                        <Dropdown  className="text-onSurface bg-surfaceContainer p-0 border-0">
                          <DropdownTrigger className="bg-surfaceVariant">
                            <Button
                              isIconOnly
                              className="h-10 w-10 bg-surfaceVariant hover:bg-onSurfaceVariant rounded-full group"
                            >
                              <MoreVertical className="w-5 h-5 text-onSurface group-hover:text-white" />
                            </Button>

                          </DropdownTrigger>
                          
                          {/* Different dropdown content based on screen size */}
                          <DropdownMenu className="p-0"
                            
                            aria-label="Project actions"
                            onAction={(key) => {
                              if (key === "view") navigate(`/session/${session.id}`);
                              else if (key === "edit") console.log("Edit", session.id);
                              else if (key === "delete") {
                                setSessionToDelete(session);
                                openDeleteModal();
                              }
                              else if (key === "regenerate") {
                                setSessionToRegenerate(session);
                                openRegenerateModal();
                              }
                              else if (key === "associate-tags") {
                                handleOpenAssociateTagModal(session);
                              }
                            }}
                          >
                            {/* Render this item only on small screens */}
                            {window.innerWidth < 768 && session.stage == "Completed" && (
                              <DropdownItem
                                key="view"
                                startContent={<Eye className="w-4 h-4" />}
                                className="text-sm p-4 py-3 rounded-xl"
                                textValue="Sitzung öffnen"
                              >
                                Sitzung Öffnen
                              </DropdownItem>
                            )}
                          
                            {/* <DropdownItem
                              key="edit"
                              startContent={<Pencil className="w-4 h-4" />}
                              className="text-sm p-4 py-3 rounded-xl"
                            >
                              Edit
                            </DropdownItem> */}
                            {/* Offer a retry for finished runs AND for failed
                                ones — a session that errored is exactly the
                                case where the user needs to re-process, and
                                gating this on "Completed" left broken rows
                                with no way forward. */}
                            { (session.stage === "Completed" || isFailedStage(session.stage)) && (
                            <DropdownItem
                              key="regenerate"
                              startContent={<RotateCcw className="w-4 h-4" />}
                              className="text-sm p-4 py-3 rounded-xl"
                              textValue={isFailedStage(session.stage) ? "Erneut versuchen" : "Regenerieren"}
                            >
                              {isFailedStage(session.stage) ? "Erneut versuchen" : "Regenerieren"}
                            </DropdownItem>
                            )}
                            <DropdownItem
                              key="associate-tags"
                              startContent={<Tags className="w-4 h-4" />}
                              className="text-sm p-4 py-3 rounded-xl"
                              textValue="Tags zuweisen"
                            >
                              Tags zuweisen
                            </DropdownItem>
                            <DropdownItem
                              key="delete"
                              startContent={<Trash className="w-4 h-4 " />}
                              className="text-sm p-4 py-3 rounded-xl"
                              color="danger"
                              textValue="Session löschen"
                            >
                              Löschen
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    </div>
                  </div>
                </li>


              );
            })}
          </ul>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Button
                size="sm"
                variant="flat"
                isDisabled={currentPage <= 1}
                onPress={() => fetchPage(currentPage - 1)}
              >
                Zurück
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === "..." ? (
                    <span key={`dots-${idx}`} className="px-1 text-sm text-default-400">...</span>
                  ) : (
                    <Button
                      key={item}
                      size="sm"
                      variant={item === currentPage ? "solid" : "flat"}
                      className={item === currentPage ? "bg-secondaryContainer text-onSecondaryContainer" : ""}
                      onPress={() => fetchPage(item)}
                    >
                      {item}
                    </Button>
                  )
                )}
              <Button
                size="sm"
                variant="flat"
                isDisabled={currentPage >= totalPages}
                onPress={() => fetchPage(currentPage + 1)}
              >
                Weiter
              </Button>
              <span className="text-xs text-default-400 ml-2">
                {totalTranscriptions} Sitzungen
              </span>
            </div>
          )}
        </div>

    </div>
  );
};

export default Home;
